import React, { FC } from 'react';
import DOMPurify from 'dompurify';

export interface IScreenShotOverlayProps {
    colorScheme?: 'dark' | 'light';
    logoUrl?: string;
    brandName?: string;
    isKeyboardTrigger?: boolean;
    warningTitle?: string;
    warningBody?: string;
    positionStyle?: 'fixed' | 'absolute';
}

const ScreenShotOverlay: FC<IScreenShotOverlayProps> = ({
    colorScheme,
    logoUrl,
    brandName,
    isKeyboardTrigger = true,
    warningTitle,
    warningBody,
    positionStyle = 'fixed'
}) => {
    const isLightTheme = colorScheme === 'light';
    const backgroundColor = isLightTheme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)';
    const textColor = isLightTheme ? '#000000' : '#ffffff';
    const textShadow = isLightTheme
        ? '1px 1px 2px rgba(255, 255, 255, 0.8)'
        : '2px 2px 4px rgba(0, 0, 0, 0.8)';
    const smallTextShadow = isLightTheme
        ? '1px 1px 2px rgba(255, 255, 255, 0.8)'
        : '1px 1px 2px rgba(0, 0, 0, 0.8)';

    const content = (
        <>
            {/* Studio Logo or Name */}
            {logoUrl ? (
                <div
                    style={{
                        marginBottom: '40px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <img
                        alt={brandName || ''}
                        src={logoUrl}
                        style={{
                            maxHeight: '220px',
                            maxWidth: '220px',
                            height: 'auto',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            ) : brandName ? (
                <div
                    style={{
                        marginBottom: '40px',
                        fontSize: '32px',
                        fontWeight: '600',
                        textShadow: smallTextShadow
                    }}
                >
                    {brandName}
                </div>
            ) : null}

            {/* Main Warning Text - Only show for keyboard triggers */}
            {isKeyboardTrigger && (
                <>
                    <div
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(warningTitle ?? '') }}
                        style={{
                            fontSize: '48px',
                            fontWeight: 'bold',
                            textShadow,
                            marginBottom: '40px'
                        }}
                    />
                    <div
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(warningBody ?? '') }}
                        style={{
                            fontSize: '24px',
                            maxWidth: '800px',
                            lineHeight: '1.6',
                            textShadow: smallTextShadow
                        }}
                    />
                </>
            )}
        </>
    );

    if (positionStyle === 'absolute') {
        return (
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    color: textColor,
                    overflowY: 'auto',
                    zIndex: 999999,
                    textAlign: 'center'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100%',
                        padding: '40px'
                    }}
                >
                    {content}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: textColor,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 999999,
                padding: '40px',
                textAlign: 'center'
            }}
        >
            {content}
        </div>
    );
};

export default ScreenShotOverlay;
